import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { Text, List, ListItem } from "@onnit-js/ui/components/text";
import { Img, Picture } from "@onnit-js/ui/components/image";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import Embed from "@onnit-js/ui/components/video/Embed";
import Sup from "@onnit-js/ui/components/text/Sup";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import MediaContentShowcase, { MediaContentShowcaseItem } from "@onnit-js/ui/components/module/MediaContentShowcase";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import config from "../../config/config";
import usePageData from "../../hooks/usePageData";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import ingredients from "./ingredients";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";

// eslint-disable-next-line func-style
function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
return null;
}

    const IMG_URL = `${onnitContext.url.cdn.static_images}/images/product-page/total-gut-health/3.0`;
    const { IMG_PARAMS } = config;
    const tghGreen = "#20A090";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/2024-TGH-Wpage-Gallery-1-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-TGH-Wpage-Gallery-1-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="Hands holding a bottle of Total Gut Health."
                        />
                        <Img
                            src={`${IMG_URL}/2024-TGH-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Woman taking a dose of Total Gut Health."
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/2024-TGH-Wpage-Gallery-3-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Woman pouring total gut health into hands."
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/2024-TGH-Wpage-Gallery-4-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-TGH-Wpage-Gallery-4-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="Hands holding a bottle of Total Gut Health."
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box bg="grays.8" py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.3" typeStyle="text02">
                                    Total GUT HEALTH<Sup variant="reg" /> with Probiotics
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Embed
                                    title="Total Gut Health Informational video"
                                    src="https://player.vimeo.com/video/995050816"
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                >
                                    Total Gut Health
                                    <Sup variant="reg" />
                                </Text>

                                <Text as="p" typeStyle="title01" color={tghGreen} mb={2}>
                                    Gut Check
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 7" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" color="grays.2" typeStyle="text02" fontWeight="bold" mb={4}>
                                    Research suggests that the gut microbiome—the balance of bacteria in your
                                    intestines—helps form the foundation for healthy immune system function. It also
                                    assists with the digestion of nutrients you need to be healthy and help transform
                                    your body.<Sup variant="dagger" />
                                </Text>
                                <Text as="p" color="grays.2" typeStyle="text02" fontWeight="bold">
                                    Total GUT HEALTH<Sup variant="reg" /> offers digestive enzymes, probiotics,
                                    prebiotics, and other
                                    nutrients that help support a healthy digestive system and gut microbiome, as well
                                    as help you process the occasional heavy meal.<Sup variant="dagger" />
                                </Text>
                            </Box>
                            <Box gridColumn="3 / span 7" px={4}>
                                <Text as="p" typeStyle="title01" color={tghGreen} mb={4}>
                                    Total GUT HEALTH<Sup variant="reg" />{" "}
                                    <Box as="br" display={["block", "block", "none", "none"]} />
                                    is for:
                                </Text>
                                <List>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Promoting a healthy gut microbiome<Sup variant="dagger" />
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Helping to digest protein, fat, and carbohydrates<Sup variant="dagger" />
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Supporting normal, healthy digestive function<Sup variant="dagger" />
                                    </ListItem>
                                </List>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <Box bg="grays.7" py={[6, 6, 6, 8]}>
                        <TwelveColumn>
                            <Box px={4} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                <Text as="h2" typeStyle="title02" color="white">
                                    Key Ingredients
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <MediaContentShowcase
                            showSlideIndex={false}
                            items={ingredients.map((item: MediaContentShowcaseItem) => {
                                const copy = { ...item };
                                copy.image = `${IMG_URL}/${item.image}`;
                                return copy;
                            })}
                            textColor="grays.3"
                            activeColor={tghGreen}
                            activeTextColor="grays.2"
                            bgMedia={(
                                <Img
                                    src={`${IMG_URL}/2024-TGH-Wpage-Ingredient-Background.png${IMG_PARAMS}`}
                                    alt=""
                                    aria-hidden="true"
                                    width={1848}
                                    height={518}
                                />
                              )}
                        />
                    </Box>

                    <Box py={[6, 6, 6, 8]}>
                        <ImageContentPortrait
                            py={[6, 6, 6, 8]}
                            reverse
                            image={(
                                <Img
                                    alt="Gamer in his room"
                                    placeholder={[464, 600]}
                                    src={`${IMG_URL}/2024-TGH-Wpage-How.png${IMG_PARAMS}`}
                                    width={926}
                                    height={1200}
                                />
                            )}
                            content={(
                                <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                    <Text as="h2" typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                        How Total Gut Health
                                        <Sup variant="reg" />{" "}Works
                                    </Text>
                                    <Text as="p" typeStyle="text02" color="grays.6" mb={[4, 4, 5]}>
                                        You can’t eat perfectly all the time. Whether you’re enjoying the occasional
                                        decadent meal or eating abroad where you’re not used to the food, you shouldn’t
                                        be punished with occasional stomach discomfort.<Sup variant="dagger" />
                                    </Text>
                                    <Text as="p" typeStyle="text02" color="grays.6" mb={[4, 4, 5]}>
                                        Total GUT HEALTH<Sup variant="reg" />{" "}helps to ease occasional bloating
                                        and keep your digestion on
                                        track. Take it at your next big meal and the effects may help save the rest of
                                        your day.<Sup variant="dagger" />
                                    </Text>
                                </Box>
                            )}
                        />
                    </Box>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />}>
                        <Text typeStyle="caption02" color="grays.5" mb={4}>
                            <Text as="span" fontWeight="bold" color="grays.5" mt={4} display="inline">WARNING:</Text> Consult a medical doctor before taking this or any other nutritional
                            supplement if vou are pregnant, nursing, have or suspect a medical condition, or are taking
                            any medications.
                            <Text as="span" fontWeight="bold" mt={4} color="grays.5">
                                KEEP OUT OF REACH OF CHILDREN.<br />Store in a cool, dry place.
                            </Text>
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
}

export default Page;
